import { getCredentials } from './cognito.js';

class DatabaseService {
  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, body = {}) {
    try {
      let credentials = AWS.config.credentials;
      
      if (!credentials || credentials.needsRefresh()) {
        console.log('Refreshing AWS credentials for database request...');
        credentials = await getCredentials();
        AWS.config.credentials = credentials;
      }
      
      const request = new AWS.HttpRequest(`${this.baseUrl}${endpoint}`, 'ap-southeast-2');
      request.method = 'POST';
      request.body = JSON.stringify(body);
      request.headers['Content-Type'] = 'application/json';
      request.headers['Host'] = new URL(`${this.baseUrl}${endpoint}`).host;
      
      const signer = new AWS.Signers.V4(request, 'execute-api');
      signer.addAuthorization(credentials, new Date());
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: request.method,
        headers: request.headers,
        body: request.body
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (err) {
      console.error('Database request failed:', err);
      throw err;
    }
  }

  collection(name) {
    return new Collection(name, this);
  }
}

class Collection {
  constructor(name, db) {
    this.name = name;
    this.db = db;
  }

  async find(query = {}, options = {}) {
    const { sort, limit, skip, ...projection } = options;
    return await this._chunkedFind(query, options);
  }

  async _chunkedFind(query = {}, options = {}) {
    const { sort, limit: requestedLimit, skip: requestedSkip = 0, _chunkSize, ...projection } = options;
    const chunkSize = _chunkSize || 50;
    let allData = [];
    let skip = requestedSkip;
    const totalLimit = requestedLimit || Infinity;
    
    while (allData.length < totalLimit) {
      const currentLimit = Math.min(chunkSize, totalLimit - allData.length);
      
      try {
        const chunk = await this.db.request(`${this.name}/read`, {
          operation_type: 'bulk',
          filter: query,
          ...(Object.keys(projection).length && { projection }),
          ...(sort && { sort: Object.entries(sort) }),
          limit: currentLimit,
          skip
        });
        
        if (chunk.length === 0) break;
        allData = allData.concat(chunk);
        skip += currentLimit;
      } catch (chunkErr) {
        console.error(`Chunk fetch failed at skip ${skip}:`, chunkErr);
        if (chunkErr.message?.includes('payload size') && chunkSize > 10) {
          return await this._chunkedFind(query, { ...options, _chunkSize: Math.floor(chunkSize / 2) });
        }
        break;
      }
    }
    
    return allData;
  }

  async count(query = {}) {
    return this.db.request(`${this.name}/count`, { 
      filter: query 
    });
  }

  async insertOne(doc) {
    return this.db.request(`${this.name}/create`, {
        operation_type: 'atomic',
        document: doc
    });
  }

  async updateOne(filter, update) {
    return this.db.request(`${this.name}/update`, {
        operation_type: 'atomic',
        filter,
        update
    });
  }
}

const getApiStage = () => {
  const hostname = window.location.hostname;
  return (hostname === 'localhost' || hostname.includes('dev.')) ? 'test' : 'prod';
};

export default new DatabaseService(`https://rkn22akwp8.execute-api.ap-southeast-2.amazonaws.com/${getApiStage()}/`);